import { bpHistory } from 'global/bpHistory';
import { autorun, makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { EnrollmentFlowDomainParams } from 'store/flows/enrollment/enrollmentFlowPage';
import { getFormInstances, requestSetAnswers } from 'api/operations/formService';
import { FormInstanceType, FormType } from 'types/formService';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import { USER_ACTIONS } from 'types/enrollmentService';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { FormObjectFromJSON, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getRootStoreInstance } from 'store/rootStore';
import { SelectProducerDelegate } from 'store/modules/pick-producer/selectProducerDelegate';
import { AgencyType } from 'types/userService';

export class SunLifeBrokerPageStore {
  private _rootStore = getRootStoreInstance();
  private _domainParams: EnrollmentFlowDomainParams;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();
  private _formInstance: FormInstanceType | null = null;

  public selectProducerDelegate = new SelectProducerDelegate();

  public producerDetailsForm = new DynamicFormDelegate({
    currentUserRoles: this._rootStore.userStore.userRoles,
    fetchForm: (): Promise<FormObjectFromJSON> => Promise.resolve(this._formInstance!.form),
    onSubmit: this.onComplete.bind(this),
  });

  public get canCompleteStep(): boolean {
    return (
      this.selectProducerDelegate.isFormValid &&
      this.producerDetailsForm.isFormValid &&
      !this.producerDetailsForm.formController?.isLoading
    );
  }

  private _setFormInstance(formInstance: FormInstanceType): void {
    this._formInstance = formInstance;
  }

  private _getAgencyDefaultFormAnswers(): Record<string, string> {
    const formAnswers = (this._formInstance?.answers.common ?? {}) as Record<string, string>;
    const { affiliation, agencyType } = this._rootStore.userStore.coreUserInfo;

    const agencyTypeValue = agencyType === AgencyType.AGENCY ? 'No' : agencyType;
    const defaultFirmName = agencyType !== AgencyType.AGENCY ? affiliation : '';

    return {
      tpa_usage_select: formAnswers.tpa_usage_select || agencyTypeValue,
      tpa_firm_name_1: formAnswers.tpa_firm_name_1 || defaultFirmName,
    };
  }

  private async _prepareThePageData(formInstance: FormInstanceType): Promise<void> {
    const formAnswers = formInstance.answers.common as Record<string, string>;

    // Prepopulate producer select
    this._setFormInstance(formInstance);
    this.selectProducerDelegate.populateProducer(formAnswers.id);

    const { id: preSavedProducerId, ...producerAnswers } = (this._formInstance?.answers.common ?? {}) as Record<
      string,
      string
    >;
    const hasPreSavedAnswers = Object.values(producerAnswers).some((answer) => answer.trim() !== '');

    if (hasPreSavedAnswers && !preSavedProducerId) {
      this.selectProducerDelegate.form.populateFields({ producerNotFound: true });
    }

    const existingFormAnswers = {
      ...formAnswers,
      ...this._getAgencyDefaultFormAnswers(),
    };

    await this.producerDetailsForm.buildForm(formInstance.id, existingFormAnswers);
  }

  private _resetAndPopulateProducerDetailsForm(selectedProducerId?: string): void {
    this.producerDetailsForm.formController?.resetForm();

    const selectedProducer = this.selectProducerDelegate.selectedProducer;
    const defaultAnswers = this._getAgencyDefaultFormAnswers();

    const agencyName =
      selectedProducer?.sponsorSpecificData?.agencyName || this._rootStore.userStore.userInfo.affiliation;

    if (selectedProducerId) {
      this.producerDetailsForm.formController?.populateFields({
        ...selectedProducer?.sponsorSpecificData,
        ...selectedProducer,
        ...defaultAnswers,
        // It's a historical mystery why this is called agency_name instead of agencyName as in other sponsors.
        // We don't ask this question and don't speak about the matter where someone might listen.
        // Just pretend you haven't seen this.
        agency_name: agencyName,
      });
    } else {
      this.producerDetailsForm.formController?.populateFields(defaultAnswers);
    }
  }

  public async getInitialData(): Promise<void> {
    this.selectProducerDelegate.form.setLoading(true);

    // Get forms & enrollment together for better performance.
    const [, formInstances] = await Promise.all([
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainParams.periodId),
      getFormInstances(this._domainParams.periodId, FormType.BROKER_FORM),
    ]);
    await runInAction(async () => {
      await this.selectProducerDelegate.requestAvailableProducers();
      await this._prepareThePageData(formInstances[0]);

      runInAction(() => {
        this.selectProducerDelegate.form.setLoading(false);
      });
    });
  }

  public resetPage(): void {
    this.selectProducerDelegate.form.resetForm();
    this.producerDetailsForm.formController?.resetForm();
  }

  public onBackRedirect(): void {
    bpHistory.push(`/checklist/${this._domainParams.periodId}`);
  }

  public async onComplete(): Promise<void> {
    this.producerDetailsForm.formController?.setLoading(true);

    const producerData: Record<string, unknown> = {
      id: this.selectProducerDelegate.selectedProducer?.id,
      ...this.producerDetailsForm.formController?.getCanonicalAnswers(),
    };

    await requestSetAnswers({
      formInstanceId: this._formInstance?.id ?? '',
      answers: {
        common: producerData,
        custom: {},
      },
      complete: true,
    });
    toastManager.addToast('Agent data saved successfully!', ToastColorTypes.SUCCESS);

    await runInAction(async () => {
      await requestApplyUserAction(this._domainParams.periodId, USER_ACTIONS.COMPLETE_BROKER);

      runInAction(() => {
        this.producerDetailsForm.formController?.setLoading(false);
        bpHistory.push(`/checklist/${this._domainParams.periodId}`);
      });
    });
  }

  constructor(domainParams: EnrollmentFlowDomainParams) {
    this._domainParams = domainParams;
    makeAutoObservable(this, {}, { autoBind: true });

    autorun(() => {
      const selectedProducerId = this.selectProducerDelegate.selectedProducer?.id;

      runInAction(() => {
        this._resetAndPopulateProducerDetailsForm(selectedProducerId);
      });
    });
  }
}

export const SunLifeBrokerPageStoreContext = createContext<SunLifeBrokerPageStore | null>(null);

export const useSunLifeBrokerPageStore = (): SunLifeBrokerPageStore => {
  const store = useContext(SunLifeBrokerPageStoreContext);
  if (!store) {
    throw new Error('useSunLifeBrokerPageStore must be used within SunLifeBrokerPageStoreContext.Provider');
  }
  return store;
};
